import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Form, Image, Row, Stack } from "react-bootstrap";
import { MdOutlineInfo } from "react-icons/md";
import { Link, createSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from "../../assets/images/logo.svg";
import CommonFormikComponent from "../../components/common/CommonFormikComponent";
import CustomCheckbox from "../../components/common/CustomCheckbox";
import CustomInputPassword from "../../components/common/CustomInputPassword";
import { default as CustomInputText } from "../../components/common/CustomInputText";
import Loader from "../../components/common/loader";
import { PASSWORD_VALIDATION } from "../../constants/messages";
import sessionStorage from "../../helpers/sessionStorage";
import storage from "../../helpers/storage";
import { loginEmailPasswordAPI } from "../../services/auth.service";
import Auth0LoginButton from "./auth0";
import { LoginSchema } from "./validation";
import { useAuthStore } from "../../redux/auth-store";
import GoogleCaptcha from "../../components/common/googleCaptcha";
import { GOOGLE_CAPTCHA_RESPONSE_KEY } from "../../constants";
import { getCaptchaToken } from "../../helpers/utils";
import { useSettingStore } from "../../redux/auth-store";

/**
 * Form values type definition.
 */
type FormValues = {
  email: string;
  password: string;
};

export default function Login() {
  const AppName = process.env.REACT_APP_NAME;

  const {
    settings: { ENABLE_RECAPTCHA, RECAPTCHA_SITE_KEY },
  } = useSettingStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const { setToken, setUser, user } = useAuthStore();

  const rememberMe: any = storage.getData("remember-me");

  /**
   * Initial Form Values
   */
  const initialValues: FormValues = {
    email: rememberMe ? rememberMe.email : "",
    password: rememberMe ? rememberMe.password : "",
  };

  useEffect(() => {
    if (storage.getToken() != null) {
      return navigate("/home");
    } else {
      localStorage.removeItem("user-info");
      storage.clearToken();
    }
  }, [navigate]);

  /**
   * Handles the change event of a checkbox input.
   * @param {ChangeEvent<HTMLInputElement>} event - The change event object.
   * @returns {void}
   */
  const onChangeCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  /**
   * Submit Method to call when user clicks on login button
   * @async
   * @param {FormValues} values
   * @param {any} actions
   * @returns {*}
   */
  const handleSubmit = async (values: Record<string, any>, actions: any) => {
    setLoading(true);
    actions.setSubmitting(true);
    const formData: any = {
      email: values.email,
      password: values.password,
    };

    /**
     * set captcha if captcha is enabled.
     */
    if (ENABLE_RECAPTCHA) {
      await getAndSetCaptchaToken(formData);
    }

    loginEmailPasswordAPI(formData)
      .then((response: any) => {
        actions.setSubmitting(false);
        if (isChecked) {
          storage.setData("remember-me", {
            email: values.email,
            password: values.password,
            isChecked,
          });
        } else {
          localStorage.removeItem("remember-me");
        }
        const { isUsing2FA } = response.data;
        if (isUsing2FA) {
          toast(response.msg, { type: toast.TYPE.INFO });
          sessionStorage.setData("2fa", values.email);
          navigate("/two-factor-authentication", {
            state: { email: values.email },
          });
        } else {
          handleLoginResponse(response);
        }
      })
      .catch((error: any) => {
        console.log(error);
        setLoading(false);
        toast(error.data.msg, { type: toast.TYPE.ERROR });
        actions.setSubmitting(false);
      });
  };

  /**
   * Handles the response after a successful login.
   * @param {Object} responseData - The response data from the login API.
   * @returns {void}
   */
  const handleLoginResponse = (responseData: any) => {
    toast(responseData.msg, { type: toast.TYPE.SUCCESS });
    setLoading(false);
    storage.setToken(responseData.data.token.access.token);
    storage.setRefreshToken(responseData.data.token.refresh.token);
    setToken("true");
    const { token, ...userDataWithoutToken } = responseData?.data || {};
    setUser(userDataWithoutToken);
    if (responseData.status === -1) {
      navigate("/my-profile");
    } else {
      navigate("/dashboard");
    }
  };

  /**
   * Retrieves and sets the captcha token in the provided form data.
   * @param {Object} formData - The form data object.
   * @returns {Promise<void>}
   */
  const getAndSetCaptchaToken = async (formData: any) => {
    const captchaToken: string = await getCaptchaToken(RECAPTCHA_SITE_KEY);
    if (captchaToken) {
      formData[GOOGLE_CAPTCHA_RESPONSE_KEY] = captchaToken;
    }
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <Row className="g-0 vh-100">
        <Col md={6}>
          <Stack
            className="custom-primary-gradient text-center justify-content-around h-100 p-4"
            gap={4}
          >
            <div className="user-select-none">
              <Image
                fluid={true}
                src={LOGO_WHITE}
                alt={`${AppName} Logo`}
                width={158}
                height={48}
              />
            </div>
            {/* <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName} Customer Panel`}
                                width={389}
                                height={334}
                            />
                        </div> */}
            {/* <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>Advanced Dynamic</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Vulnerability Scanning</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div> */}
          </Stack>
        </Col>
        <Col md={6} className="col-md-6 d-flex flex-column p-3">
          <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
            <div className="mt-135">
              <p className="mb-1 opacity-75 fs-14 ">Welcome to</p>
              <h1 className="fw-semibold mb-4 pb-1 fs-2">Admin Panel</h1>
              <CommonFormikComponent
                validationSchema={LoginSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <>
                    <CustomInputText
                      wrapperClassname="mb-3 pb-1"
                      controlId="email"
                      label="Email Address"
                      placeholder="john.smith@example.com"
                      touched={formikProps.touched.email}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.email}
                      value={formikProps.values.email}
                      validationSchema={LoginSchema}
                      autoComplete="email"
                    />

                    <CustomInputPassword
                      wrapperClassname="mb-3"
                      controlId="password"
                      label="Password"
                      touched={formikProps.touched.password}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.password}
                      value={formikProps.values.password}
                      validationSchema={LoginSchema}
                      tooltip={PASSWORD_VALIDATION}
                      tooltipContent={<MdOutlineInfo />}
                      autoComplete="current-password"
                    />

                    <Form.Group className="mb-3 pb-3">
                      <Row
                        xs="auto"
                        className="align-items-center justify-content-between gx-0"
                      >
                        <Col>
                          <CustomCheckbox
                            controlId="rememberCheck"
                            value="Remember Me"
                            label="Remember Me"
                            checked={isChecked}
                            onChange={onChangeCheckbox}
                          />
                        </Col>
                        <Col>
                          <Link
                            className="fs-13 fw-semibold text-decoration-none"
                            to="/forgot-password"
                          >
                            Forgot Password?
                          </Link>
                        </Col>
                      </Row>
                    </Form.Group>
                    <Form.Group className="mb-0 pb-0">
                      <Row className="align-items-center justify-content-between ps-2">
                        <GoogleCaptcha />
                      </Row>
                    </Form.Group>
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="justify-content-end"
                    >
                      <Button
                        className="w-100"
                        variant="primary"
                        type="submit"
                        disabled={formikProps.isSubmitting || loading}
                      >
                        {loading ? "Processing..." : "Login"}
                      </Button>
                    </Stack>
                    {/* <Auth0LoginButton /> */}
                  </>
                )}
              </CommonFormikComponent>
            </div>
            {/* <div className='bg-dark bg-opacity-05 custom-min-height-40 fs-13 p-2 rounded text-center w-100 d-inline-flex align-items-center justify-content-center'>
                            <span className='pe-1'>New User?</span> <Link className="fw-semibold text-decoration-none" to="/create-account">Create an Account</Link>
                        </div> */}
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
